import React, { useEffect, useState, useCallback, useRef, Fragment } from 'react'
import { useIntl } from 'react-intl'
import Page from 'contexts/page'
import { useDocuments } from 'contexts/documents'
import { useCheckDocumentPermission } from 'pages/documents/utilities'
import { useAccess } from 'contexts/access'
import { useUpgradable } from 'hooks/upgradable'
import { useDragToUpload } from 'pages/documents/hooks/drag-to-upload'
import { useQueryResolveItem } from 'hooks/resolve-item'
import { useLocationStateIntent } from 'hooks/location-intent'
import { useAppSignal } from 'hooks/app-signal'
import paths from 'app/paths'
import { getSmartDocumentUrl } from 'utilities/url'
import Tutorial from 'components/tutorial'
import { HeroActions } from './s'
import AddDocument from 'pages/documents/modals/add-document'
import Filter, { areFiltersApplied } from 'pages/documents/components/documents-filter'
import { ButtonArrow } from 'components/button'
import Ellipsify from 'components/ellipsify'
import Documents from 'pages/documents/lists/documents'
import PreviewDocument from 'pages/documents/modals/preview'
import Modal from 'modals/dialog'
import ConfigureDocumentSigning from 'pages/documents/modals/configure-signing'
import DocumentSigningStatus from 'pages/documents/modals/signing-status'
import BulkPanel from 'pages/documents/panels/bulk-document'
import File from 'components/file'
import {
    Eye as Preview,
    Edit,
    Link as CopyLink,
    Trash2 as Delete
} from 'styled-icons/feather'
import {
    SettingsDocument,
    Signature
} from 'components/feather'

const salt = 'documents'

const DocumentsPage = () => {
    const { formatMessage } = useIntl()

    const wrapper = useRef()

    const {
        fetch,
        dokuments,
        selection,
        filter,
        hasFetched,

        setDocumentsFilter,
        fetchDocument,
        getSignedDocumentDownloadUrl,
        removeDocument
    } = useDocuments()

    const {
        checkFeature,
        check
    } = useAccess()

    const checkUpgradable = useUpgradable()

    const smartDocumentsAvailable = checkFeature('smart-documents')
    const smartDocumentsUpgradable = checkUpgradable({ feature: 'smart-documents' })

    const signingAvailable = checkFeature('digital-signing')
    const signingUpgradable = checkUpgradable({ feature: 'digital-signing' })
    const signingAccess = check('digital-signing:manage')// || check('unit:document-signing:manage')

    const checkDocumentPermission = useCheckDocumentPermission()

    const [hasFetchedInitially, setHasFetchedInitially] = useState(false)
    const [addingFile, setAddingFile] = useState(false)
    const [addingSmart, setAddingSmart] = useState(null)
    const [configuringSigning, setConfiguringSigning] = useState(null)
    const [previewing, setPreviewing] = useState(null)
    const [reviewingDocumentSigningStatus, setReviewingDocumentSigningStatus] = useState(null)

    // If you enter this pane, it’s assumed you have access and it’s not upgradable
    useEffect(() => {
        if(!hasFetched && !hasFetchedInitially) {
            setHasFetchedInitially(true)
            fetch()
        }
    }, [hasFetched, hasFetchedInitially])

    const {
        resetLocalDrag,
        resetGlobalDrag
    } = useDragToUpload({
        ref: wrapper,
        setUploading: show => setAddingFile(show)
    })

    useQueryResolveItem({
        items: dokuments,
        fetch: fetchDocument,
        onResolved: (dokument, clearQuery) => {
            dokument && setPreviewing({ dokument })
            clearQuery()
        }
    })

    useLocationStateIntent({
        intent: 'show-mine',
        action: () => setDocumentsFilter({ onlyOwnerId: 'me' }, { external: true })
    })

    useLocationStateIntent({
        intent: 'upload',
        action: () => setAddingFile(true),
        requirements: [wrapper.current]
    })

    useAppSignal({
        namespace: `document:updated:${previewing?.dokument?.id}`,
        action: dokument => setPreviewing({ dokument, updatedAt: Date.now() })
    })

    useAppSignal({
        namespace: `document:updated:${reviewingDocumentSigningStatus?.dokument?.id}`,
        action: dokument => setReviewingDocumentSigningStatus({ dokument })
    })

    useAppSignal({
        namespace: 'document:preview',
        action: dokument => setPreviewing({ dokument })
    })

    const reset = useCallback(() => {
        resetLocalDrag()
        resetGlobalDrag()
    }, [])

    const actions = {
        // That this key is `preview` is important as it is unpacked and used for the item click
        preview: dokument => ({
            salt: `${salt}:preview`,
            icon: <Preview size={24} />,
            label: formatMessage({
                id: 'action_preview',
                defaultMessage: 'Preview'
            }),
            effect: 'neutral',
            onClick: () => setPreviewing({ dokument })
        }),
        editDocument: dokument => {
            if(dokument.type !== 'smart' || (!smartDocumentsAvailable && !smartDocumentsUpgradable)) {
                return null
            }

            if(!checkDocumentPermission(dokument).editable) {
                return null
            }

            return {
                salt: `${salt}:editor`,
                icon: <Edit size={24} />,
                label: formatMessage({
                    id: 'document_action_edit',
                    defaultMessage: 'Edit document'
                }),
                element: 'link',
                link: {
                    to: getSmartDocumentUrl(dokument)
                },
                effect: 'neutral',
                ...(smartDocumentsUpgradable ? {
                    upgrade: {
                        feature: 'smart-documents'
                    }
                } : null)
            }
        },
        settings: dokument => {
            if(!checkDocumentPermission(dokument).editable) {
                return null
            }

            const smart = dokument.type === 'smart'
            if(smart && (!smartDocumentsAvailable && !smartDocumentsUpgradable)) {
                return null
            }

            return {
                salt: `${salt}:settings`,
                icon: <SettingsDocument size={24} />,
                label: formatMessage({
                    id: 'document_action_settings',
                    defaultMessage: 'Document settings'
                }),
                effect: 'neutral',
                onClick: () => setPreviewing({ dokument, mode: 'edit' }),
                ...((smart && smartDocumentsUpgradable) ? {
                    upgrade: {
                        feature: 'smart-documents'
                    }
                } : null)
            }
        },
        configureSigning: dokument => {
            if(!signingAvailable && !signingUpgradable) {
                return null
            }

            if(!!dokument?.signing || dokument.extension !== 'pdf' || !checkDocumentPermission(dokument).signable) {
                return null
            }

            return {
                salt: `${salt}:configure-signing`,
                icon: <Signature size={24} />,
                label: formatMessage({
                    id: 'document_action_send_for_signing',
                    defaultMessage: 'Send for signing'
                }),
                effect: 'neutral',
                onClick: () => setConfiguringSigning({ dokument }),
                ...(signingUpgradable ? {
                    upgrade: {
                        feature: 'digital-signing'
                    }
                } : null)
            }
        },
        copyLink: dokument => ({
            salt: `${salt}:copy-link`,
            icon: <CopyLink size={24} />,
            label: formatMessage({
                id: 'action_copy_link',
                defaultMessage: 'Copy link'
            }),
            effect: 'neutral',
            onClick: () => global.navigator.clipboard.writeText(`${global.location.origin}${paths.documents.base}?id=${dokument.id}`)
        }),
        remove: ({ id, name, extension, signing, ...dokument }) => {
            if(!checkDocumentPermission(dokument).editable) {
                return null
            }

            const fileName = `${name}.${extension}`

            return {
                salt: `${salt}:delete`,
                icon: <Delete size={24} />,
                label: formatMessage({
                    id: 'action_delete',
                    defaultMessage: 'Delete',
                }),
                effect: 'destructive',
                onClick: () => removeDocument(id),
                prompt: {
                    question: formatMessage({
                        id: 'document_action_delete_prompt_question',
                        defaultMessage: 'Confirm deleting {document}'
                    }, {
                        document: (
                            <Ellipsify
                                text={fileName}
                                head={9}
                                tail={9}
                                tooltipProps={{ maxWidth: 'none' }} />
                        )
                    }),
                    ...((signing?.status === 'pending') ? {
                        message: formatMessage({
                            id: 'document_action_delete_prompt_message_signed_pending',
                            defaultMessage: 'This document is still waiting for signatures. The signing process will be stopped when deleting.'
                        })
                    } : null),
                    ...((signing?.status === 'closed') ? {
                        message: formatMessage({
                            id: 'document_action_delete_prompt_message_signed_closed',
                            defaultMessage: 'The signed document will be deleted. This can not be undone.\n\nDo you want to download it first?'
                        }),
                        content: (
                            <File
                                meta={{ fileName }}
                                download={{
                                    path: getSignedDocumentDownloadUrl(id),
                                    generateName: () => fileName
                                }}
                                className="spacious" />
                        )
                    } : null),
                    confirmText: formatMessage({
                        id: 'document_action_delete_prompt_confirm',
                        defaultMessage: 'Delete'
                    })
                }
            }
        }
    }

    const filtersApplied = areFiltersApplied(filter)
    const selected = !!selection.length

    return (
        <>
            <div ref={wrapper}>
                <HeroActions {...(selected ? { className: 'has-selected' } : null)}>
                    <Filter salt={salt} />
                    <Fragment>
                        <AddDocument
                            addingFile={addingFile}
                            addingSmart={addingSmart}
                            setAddingFile={setAddingFile}
                            setAddingSmart={setAddingSmart}
                            setConfiguringSigning={setConfiguringSigning}
                            reset={reset}
                            salt={salt} />
                        {(!dokuments?.length && hasFetched && !filtersApplied) && (
                            <ButtonArrow />
                        )}
                    </Fragment>
                </HeroActions>
                <Documents
                    header={(!!dokuments?.length && hasFetched)}
                    actions={actions}
                    {...(selected ? { className: 'has-selected' } : null)} />
                {!!signingAccess && (
                    <Modal
                        show={!!configuringSigning}
                        dismiss={() => setConfiguringSigning(null)}
                        className="scrollable"
                        salt={`${salt}:configure-signing`}>
                        <ConfigureDocumentSigning
                            {...configuringSigning}
                            onDone={() => setConfiguringSigning(null)}
                            salt={`${salt}:configure-signing`} />
                    </Modal>
                )}
                <Modal
                    show={!!previewing}
                    dismiss={() => setPreviewing(null)}
                    className="fullscreen compact"
                    salt={`${salt}:preview`}>
                    <PreviewDocument
                        {...previewing}
                        setConfiguringSigning={setConfiguringSigning}
                        setReviewingDocumentSigningStatus={setReviewingDocumentSigningStatus}
                        previewAnother={dokument => setPreviewing({ dokument })}
                        removeAction={actions.remove}
                        salt={`${salt}:preview`}
                        key={`${salt}:preview:${previewing?.dokument?.id ?? 'none'}`} />
                </Modal>
                <Modal
                    show={!!reviewingDocumentSigningStatus}
                    dismiss={() => setReviewingDocumentSigningStatus(null)}
                    className="scrollable"
                    salt={`${salt}:review-document-signing-status`}>
                    <DocumentSigningStatus
                        {...reviewingDocumentSigningStatus}
                        salt={`${salt}:review-document-signing-status`} />
                </Modal>
            </div>
            <BulkPanel salt={salt} />
        </>
    )
}

export default ({ upgradable, ...props }) => (
    <Page
        view="documents"
        title={{
            id: 'documents_heading',
            defaultMessage: 'Documents'
        }}
        upgrade={{
            enabled: upgradable,
            content: (
                <Tutorial
                    which="documents"
                    upgrade={{ module: 'documents' }} />
            )
        }}>
        <DocumentsPage {...props} />
    </Page>
)